// The app's sign-in return address and how to read what comes back on it.
//
// "Continue with Google" / "Continue with Apple" in the app (2026-11-02) run
// Supabase's own OAuth flow in the phone's browser sheet — no Google Android
// client, no signing-certificate fingerprint, the same Web client the website
// uses — and come back to the app on this custom scheme. Supabase hands back
// either a PKCE `?code=` or (implicit flow, this client's default) the session
// in the fragment, or an error in either place. `provider` is our own query
// parameter on the redirect, so a failed link knows which door to retry as a
// plain sign-in.

export const AUTH_SCHEME = "com.raisedbeef.app";
export const AUTH_DEEP_LINK = `${AUTH_SCHEME}://auth`;

export type AuthProvider = "apple" | "google";

export type AuthReturn =
  | { kind: "code"; code: string; provider: AuthProvider | null }
  | { kind: "tokens"; access_token: string; refresh_token: string; provider: AuthProvider | null }
  | { kind: "error"; code: string; description: string; provider: AuthProvider | null };

/** The redirect address for one provider's flow. */
export const authRedirectFor = (provider: AuthProvider): string => `${AUTH_DEEP_LINK}?provider=${provider}`;

/** null when the address is not ours. */
export function parseAuthReturn(url: string): AuthReturn | null {
  if (!url.startsWith(AUTH_DEEP_LINK)) return null;
  const q = url.indexOf("?");
  const h = url.indexOf("#");
  const query = new URLSearchParams(q !== -1 ? url.slice(q + 1, h !== -1 && h > q ? h : undefined) : "");
  const frag = new URLSearchParams(h !== -1 ? url.slice(h + 1) : "");
  const get = (k: string) => query.get(k) ?? frag.get(k);
  const p = get("provider");
  const provider: AuthProvider | null = p === "apple" || p === "google" ? p : null;
  const err = get("error_code") ?? get("error");
  if (err) return { kind: "error", code: err, description: get("error_description") ?? "", provider };
  const code = get("code");
  if (code) return { kind: "code", code, provider };
  const access_token = frag.get("access_token");
  const refresh_token = frag.get("refresh_token");
  if (access_token && refresh_token) return { kind: "tokens", access_token, refresh_token, provider };
  return { kind: "error", code: "empty", description: "", provider };
}
